import classNames from 'classnames';
import { IconChevron, IconChevronDown } from 'components/Icons';
import { useNTContext } from '../../NTContext';
import { useNTUtilsContext } from '../../NTUtilsContext';
import { useNTSelectionContext } from '../../NTSelectionContext';
import { ReportRow } from 'types/report';
import { prefixClass } from 'lib/utils';
import Sparkline from '../Sparklines';

import './FormatAccounts.scss';
import { handleCollapseRow } from '../../utils/tableUtils';
import { Whisper, Tooltip } from 'rsuite';
import { formatWhisperTooltip, WhisperTooltip } from '../FormatReportValues/formatReportValuesUtils';
import { useEffect, useState, memo } from 'react';
import { useActiveReportStore } from 'hooks/useActiveReportStore';
import { VendorStatementFormModalContainer } from '../../../VendorStatementFormModal';

const FormatAccounts = ({
  cellData,
  cellID,
  rowID,
  allColumn,
}: {
  cellData: ReportRow;
  cellID: string | null;
  rowID?: string | null;
  allColumn?: any;
}) => {
  const prefix = prefixClass('account-values');
  const { filters, columns, sparkLine, budgetAvailable } = useNTContext();
  const { actions, capabilities, setActions, accountVisualFilters } = useNTUtilsContext();
  const { cellStates } = useNTSelectionContext();
  const reportPayload = useActiveReportStore(state => state.reportPayload);
  const { amountTitle, percentTitle, level, children, glCode, display, vizHelpers, amounts } = cellData;
  const { isBigHeader, isHeader, lastItem, isTotal, isExpanded } = vizHelpers;
  const [chartData, setChartData] = useState(amounts?.map(item => item.amount));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalPosition, setModalPosition] = useState<{ top: number; left: number } | null>(null);

  // Check if this is a Vendor Invoices report
  const isVendorInvoicesReport = reportPayload?.templateId === 'Vendor Invoices';

  // Check if this row is selected (for showing the button)
  const isRowSelected = cellStates.selected === rowID;

  /*
    Here we calculate the chart data.
    Backend handles column reversal - data already in correct order
  */
  useEffect(() => {
    // First we create a copy of the array so we fixed the issue with the reference
    const localAmounts = amounts ? [...amounts] : [];
    const localChartData = localAmounts
      ? // If we are representing a budget, the data available for account column still has amounts and budgets in different blocks, so we need to filter out the budgets
        budgetAvailable && localAmounts.length > 2
        ? // if we only have 2 items, that means that we can plot the budget and the amount in the same chart
          localAmounts.filter((item, index) => index % 2).map(item => (item.amount !== null ? item.amount : 0))
        : localAmounts.map(item => (item.amount !== null ? item.amount : 0))
      : new Array(columns).fill(0);
    if (actions.total || actions.difference) localChartData.pop();
    setChartData(localChartData);
  }, [amounts, actions.total, actions.difference, columns, budgetAvailable]);

  const hasGlCode = glCode && glCode !== '0';

  const tooltipContents: WhisperTooltip[] = [];
  if (hasGlCode) {
    tooltipContents.push({ title: `GL ${glCode}`, value: '' });
  }
  tooltipContents.push({ title: amountTitle, value: '', style: { fontWeight: 'bold' } });
  if (filters.percentage && percentTitle) tooltipContents.push({ title: `% of ${percentTitle}`, value: '' });

  const accountTooltip = formatWhisperTooltip(tooltipContents);

  // Modal handlers
  const handleRequestStatement = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Calculate position relative to the clicked button
    const buttonElement = e.currentTarget;
    const rect = buttonElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    // Calculate optimal position
    const modalWidth = 410; // Actual modal width from CSS positioned-modal
    const modalMaxHeight = 500; // Max height from CSS positioned-modal
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 20; // Minimum padding from viewport edges

    let left = rect.right + scrollLeft + 10; // Position to the right with 10px gap
    let top = rect.top + scrollTop - 75; // Position 75px above the button

    // Adjust if modal would go off-screen horizontally
    if (left + modalWidth > viewportWidth + scrollLeft - padding) {
      left = rect.left + scrollLeft - modalWidth - 10; // Position to the left instead
    }

    // Ensure left is within bounds
    left = Math.max(padding, Math.min(left, viewportWidth + scrollLeft - modalWidth - padding));

    // Adjust if modal would go off-screen vertically
    // Keep minimum distance from top
    if (top < scrollTop + padding) {
      top = scrollTop + padding;
    }

    // Keep minimum distance from bottom
    // Subtract padding to ensure modal doesn't touch the bottom edge
    if (top + modalMaxHeight > viewportHeight + scrollTop - padding) {
      top = viewportHeight + scrollTop - modalMaxHeight - padding;
    }

    // Final bounds check
    top = Math.max(padding, top);

    setModalPosition({ top, left });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div
      className={classNames(prefix('KS-account-cell'), {
        'extra-height': isBigHeader,
        total: isTotal,
      })}
      data-cell-id={cellID}
      data-status={false}
    >
      <div className={prefix('indent-container')}>
        {Array.from({ length: level }).map((_, index) => (
          <div key={index} className={prefix('indent-element')}>
            <div
              className={classNames(prefix('indent-element-shape'), {
                ['level-line']: index < level - 1,
                ['connection-line']: index === level - 1 && !lastItem,
                ['last-item-connection-line']: index === level - 1 && lastItem,
              })}
            ></div>
          </div>
        ))}
      </div>
      <div
        className={classNames(prefix('account-children'))}
        onClick={e =>
          handleCollapseRow({ e: e, rowID: rowID as string, cellDataID: cellData.id, allColumn: allColumn })
        }
        data-collapse-control={rowID}
      >
        {children && children.length > 0 ? (
          <div className={`${prefix('account-children-icons')}`}>
            <div className={`${prefix('account-children-icons-icon')}`}>
              <IconChevron />
            </div>
            <div className={`${prefix('account-children-icons-icon')}`}>
              <IconChevronDown />
            </div>
          </div>
        ) : (
          ''
        )}
      </div>
      <div className={prefix('account-label')}>
        {accountVisualFilters.accounts && hasGlCode && !isBigHeader && (
          <div
            className={prefix('account-label-gl-code')}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {glCode}
          </div>
        )}
        <Whisper
          placement="top"
          controlId="control-id-hover"
          trigger="hover"
          delayOpen={800}
          speaker={<Tooltip>{accountTooltip}</Tooltip>}
        >
          <div
            /* title={amountTitle}  this is the way to trigger the default browser tooltip*/
            className={classNames(prefix('account-label-title'), {
              'l-total': isTotal,
              'l-header': isHeader,
              'l-big-header': isBigHeader,
            })}
          >
            {amountTitle}
          </div>
        </Whisper>
        {filters.percentage && accountVisualFilters.percentageLegend && percentTitle && !isBigHeader && (
          <div className={prefix('account-label-percent-title')}>% of {percentTitle}</div>
        )}
        {/* Show REQUEST STATEMENT button for vendor invoices report when row is selected */}
        {isVendorInvoicesReport && isRowSelected && !isBigHeader && !isHeader && !isTotal && (
          <button className={prefix('request-statement-btn')} onClick={handleRequestStatement}>
            REQUEST STATEMENT
          </button>
        )}
      </div>
      {accountVisualFilters.sparklines &&
        capabilities.sparklines !== 'none' &&
        !isBigHeader &&
        chartData &&
        chartData.length > 1 && (
          <div
            className={prefix('sparkline-container')}
            onClick={e => {
              if (!actions.detailsPanel) {
                setActions({ ...actions, detailsPanel: true });
              } else {
                if (cellStates.selected === rowID) {
                  setActions({ ...actions, detailsPanel: false });
                }
              }
            }}
          >
            <Sparkline
              type={capabilities.sparklines}
              data={chartData as unknown as number[]}
              width={columns > 6 ? (columns * 40) / 15 : 16}
              height={16}
              showZeroLine
            />
          </div>
        )}
      {/* Vendor Statement Modal */}
      {isVendorInvoicesReport && (
        <VendorStatementFormModalContainer
          isOpen={isModalOpen}
          vendorId={cellData.vendorId}
          accountId={cellData.vendorId}
          position={modalPosition}
          onClose={handleCloseModal}
          vendorName={cellData.refTitle}
        />
      )}
    </div>
  );
};

// Memoize to prevent re-renders when parent re-renders (e.g., on scroll)
// Props only change when actual cell data changes, not on parent updates
export default memo(FormatAccounts);
